import {
  Button,
  Heading,
  HStack,
  Skeleton,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CreateBranchModal } from './CreateBranchModal';
import { branchesApi, Branch } from '@texas/api/endpoints/branchesApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useValueDisclosure } from '@texas/hooks/useValueDisclosure';
import { Icons } from '@texas/components/shared/Icons';

export function AvailableBranches({
  onBranchCreated,
}: {
  onBranchCreated?: (branch: Branch) => void;
}) {
  const { t } = useTranslation();
  const {
    data: availableBranches,
    refetch: refetchAvailableBranches,
    set: setAvailableBranches,
    loading,
  } = useApiResource(branchesApi.getAvailableBranchesForCreation);

  useEffect(() => {
    refetchAvailableBranches();
  }, [refetchAvailableBranches]);

  const {
    value: branchValue,
    isOpen,
    onOpen,
    onClose,
  } = useValueDisclosure<string>();

  return (
    <>
      {branchValue && (
        <CreateBranchModal
          isOpen={isOpen}
          onClose={onClose}
          onBranchCreated={function (branch: Branch): void {
            onClose();
            onBranchCreated?.(branch);
            const set =
              availableBranches?.filter((b) => b !== branch.identifier) ?? [];
            setAvailableBranches(set);
          }}
          identifier={branchValue}
        />
      )}

      <Skeleton isLoaded={!loading}>
        <HStack>
          {availableBranches && (
            <>
              {availableBranches.length === 0 ? (
                <HStack color="entityDirectory.mutedText">
                  <Icons.InformationOutline />
                  <Text fontSize={12} textTransform="uppercase">
                    {t('branch.noNewBranchesAvailable')}
                  </Text>
                </HStack>
              ) : (
                <VStack
                  w="100%"
                  alignItems="start"
                  backgroundColor="entityDirectory.raisedSurface"
                  border="1px solid"
                  borderColor="entityDirectory.border"
                  borderRadius={0}
                  padding={2}
                >
                  <Heading size="sm" color="entityDirectory.text">
                    {t('branch.newBranchesAvailable')}
                  </Heading>
                  <Text fontSize={12} color="entityDirectory.mutedText">
                    {t('branch.clickToCreate')}
                  </Text>
                  <HStack>
                    {availableBranches.map((b) => (
                      <Button
                        size="sm"
                        variant="entityDirectoryPrimary"
                        key={b}
                        onClick={() => onOpen(b)}
                      >
                        {b}
                      </Button>
                    ))}
                  </HStack>
                </VStack>
              )}
            </>
          )}
        </HStack>
      </Skeleton>
    </>
  );
}
